// apps/workflow/src/pages/designer/Tasks.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  Search,
  Filter,
  Eye,
  Check,
  Clock,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Briefcase,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

// ─── Helpers ──────────────────────────────────────────────────────
const getStatusBadge = (status) => {
  const config = {
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800' },
    assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-800' },
    'in-progress': { label: 'In Progress', color: 'bg-indigo-100 text-indigo-800' },
    submitted: { label: 'Submitted', color: 'bg-purple-100 text-purple-800' },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
  };
  return config[status] || { label: status || 'Unknown', color: 'bg-slate-100 text-slate-800' };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

// ─── Main Component ──────────────────────────────────────────────
const DesignerTasks = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userRole = user?.role || 'designer';
  const isAdmin = ['admin', 'super_admin', 'manager'].includes(userRole);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('all'); // all, available, my, completed

  const fetchTasks = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const records = await pb.collection('project_tasks').getFullList({
        sort: '-created',
        expand: 'project,project.client,assignedTo,completedBy,submittedBy',
        requestKey: null,
      });
      setTasks(records);
    } catch (error) {
      if (!error.isAbort) {
        console.error('Error fetching tasks:', error);
        toast.error('Failed to load tasks');
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchTasks();
    setRefreshing(false);
    toast.success('Tasks refreshed');
  };

  // ─── Filtering ──────────────────────────────────────────────
  const filteredTasks = useMemo(() => {
    let list = tasks;

    // Tab filter
    if (activeTab === 'available') {
      list = list.filter((t) => !t.assignedTo && t.status !== 'completed' && t.status !== 'submitted');
    } else if (activeTab === 'my') {
      list = list.filter((t) => t.assignedTo === user?.id);
    } else if (activeTab === 'completed') {
      list = list.filter((t) => t.status === 'completed' && t.completedBy === user?.id);
    }
    // 'all' – show everything (admin sees all, others see only relevant? we'll show all but maybe filter based on role later)

    // Search
    if (searchTerm) {
      list = list.filter((t) =>
        t.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.expand?.project?.projectName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      list = list.filter((t) => t.status === filterStatus);
    }

    // Category filter
    if (filterCategory !== 'all') {
      list = list.filter((t) => t.category === filterCategory);
    }

    return list;
  }, [tasks, activeTab, searchTerm, filterStatus, filterCategory, user]);

  const stats = useMemo(() => {
    const total = tasks.length;
    const available = tasks.filter((t) => !t.assignedTo && t.status !== 'completed' && t.status !== 'submitted').length;
    const my = tasks.filter((t) => t.assignedTo === user?.id).length;
    const completed = tasks.filter((t) => t.status === 'completed' && t.completedBy === user?.id).length;
    return { total, available, my, completed };
  }, [tasks, user]);

  // ─── Category options ──────────────────────────────────────
  const categories = useMemo(() => {
    const catSet = new Set(tasks.map((t) => t.category).filter(Boolean));
    return ['all', ...Array.from(catSet)];
  }, [tasks]);

  // ─── Loading ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 font-medium">Loading tasks...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-indigo-600" />
            All Tasks
          </h1>
          <p className="text-sm text-slate-500">Manage and track design tasks</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 text-slate-400 hover:text-indigo-600 transition rounded-lg hover:bg-indigo-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard title="Total" value={stats.total} icon={ClipboardList} color="indigo" />
        <StatCard title="Available" value={stats.available} icon={AlertCircle} color="amber" />
        <StatCard title="My Tasks" value={stats.my} icon={Clock} color="blue" />
        <StatCard title="Completed" value={stats.completed} icon={CheckCircle} color="green" />
      </div>

      {/* Tabs & Filters */}
      <div className="flex flex-wrap items-center gap-4 bg-white p-3 rounded-xl shadow-sm border border-slate-200">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition ${
              activeTab === 'all'
                ? 'bg-white shadow text-indigo-600'
                : 'text-slate-600 hover:bg-slate-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveTab('available')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition ${
              activeTab === 'available'
                ? 'bg-white shadow text-indigo-600'
                : 'text-slate-600 hover:bg-slate-200'
            }`}
          >
            Available
          </button>
          <button
            onClick={() => setActiveTab('my')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition ${
              activeTab === 'my'
                ? 'bg-white shadow text-indigo-600'
                : 'text-slate-600 hover:bg-slate-200'
            }`}
          >
            My Tasks
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition ${
              activeTab === 'completed'
                ? 'bg-white shadow text-indigo-600'
                : 'text-slate-600 hover:bg-slate-200'
            }`}
          >
            Completed
          </button>
        </div>

        <div className="flex-1 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[150px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="assigned">Assigned</option>
            <option value="in-progress">In Progress</option>
            <option value="submitted">Submitted</option>
            <option value="completed">Completed</option>
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat === 'all' ? 'All Categories' : cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Task List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        {filteredTasks.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">No tasks match your filters.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTasks.map((task) => {
              const status = getStatusBadge(task.status);
              const project = task.expand?.project;
              return (
                <div
                  key={task.id}
                  className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition cursor-pointer"
                  onClick={() => navigate(`/designer/task/${task.id}`)}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-800">{task.title}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-slate-500 flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {project?.projectName || 'Project'}
                        </span>
                        {task.floorNumber && (
                          <span className="text-xs">Floor {task.floorNumber}</span>
                        )}
                        {task.category && (
                          <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full">
                            {task.category}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">
                        {task.assignedTo ? `Assigned` : 'Unassigned'}
                      </span>
                      <Eye className="w-4 h-4 text-indigo-500" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── StatCard ──────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color }) => {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-green-50 text-green-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${colors[color] || 'bg-slate-50 text-slate-600'}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

export default DesignerTasks;